import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import axios from "axios";
import { Calendar, Users, Phone, User, Mail, CheckCircle2, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL.replace(/\/$/, '')}/api`;

// WhatsApp numbers for booking confirmations
const WHATSAPP_NUMBERS = ["+919207641616", "+919582399203"];

function BookingApp() {
  const [form, setForm] = useState({
    checkin: "",
    checkout: "",
    guests: 1,
    cottages: 1, // Number of cottages (optional, for cottage bookings)
    room: "cottage",
    name: "",
    phone: "",
    email: "",
  });

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState(null);

  // Pricing structure
  const getPricing = (room, isWeekend, guests, cottages = 1) => {
    const prices = {
      cottage: {
        weekend: 4999,
        weekday: 3999,
        maxGuests: null,
        overflowRate: 0,
      },
      dormitory: {
        weekend: 19999,
        weekday: 17999,
        maxGuests: 16,
        overflowRate: 1200,
      },
      full_property: {
        weekend: 29999,
        weekday: 27999,
        maxGuests: 25,
        overflowRate: 1200,
      },
    };

    const roomPricing = prices[room];
    const basePrice = isWeekend ? roomPricing.weekend : roomPricing.weekday;
    
    let overflowCharge = 0;
    if (roomPricing.maxGuests && guests > roomPricing.maxGuests) {
      const extraGuests = guests - roomPricing.maxGuests;
      overflowCharge = extraGuests * roomPricing.overflowRate;
    }

    // For cottages, multiply the total by number of cottages
    const singleRoomPrice = basePrice + overflowCharge;
    if (room === 'cottage') {
      return singleRoomPrice * cottages;
    }

    return singleRoomPrice;
  };

  // Check if date is weekend (Friday or Saturday)
  const isWeekend = (date) => {
    const day = new Date(date).getDay();
    return day === 5 || day === 6; // Friday = 5, Saturday = 6
  };

  // Calculate total price for all nights
  const calculateTotal = () => {
    if (!form.checkin || !form.checkout) return 0;

    const checkinDate = new Date(form.checkin);
    const checkoutDate = new Date(form.checkout);
    const nights = Math.ceil((checkoutDate - checkinDate) / (1000 * 60 * 60 * 24));

    if (nights <= 0) return 0;

    // Parse cottages as integer (input returns string)
    const cottagesCount = parseInt(form.cottages) || 1;

    let total = 0;
    for (let i = 0; i < nights; i++) {
      const currentDate = new Date(checkinDate);
      currentDate.setDate(currentDate.getDate() + i);
      const nightPrice = getPricing(form.room, isWeekend(currentDate), form.guests, cottagesCount);
      total += nightPrice;
    }

    return total;
  };

  const nights = form.checkin && form.checkout
    ? Math.max(0, Math.ceil((new Date(form.checkout) - new Date(form.checkin)) / (1000 * 60 * 60 * 24)))
    : 0;

  const total = calculateTotal();

  // Fetch bookings from backend
  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    
    // Only reset availability when dates, room, guests, or cottages change
    // Don't reset when name, phone, or email change
    if (['checkin', 'checkout', 'guests', 'cottages'].includes(name)) {
      setAvailabilityStatus(null);
    }
  };

  // Check availability
  const checkAvailability = async () => {
    if (!form.checkin || !form.checkout) {
      alert("Please select check-in and check-out dates");
      return;
    }

    setCheckingAvailability(true);
    try {
      const response = await axios.post(`${API}/bookings/check-availability`, {
        checkin: new Date(form.checkin).toISOString(),
        checkout: new Date(form.checkout).toISOString(),
        room: form.room,
        cottages: parseInt(form.cottages) || 1,
      });

      setAvailabilityStatus(response.data);
    } catch (error) {
      console.error("Error checking availability:", error);
      setAvailabilityStatus({
        available: false,
        message: "Error checking availability. Please try again.",
      });
    } finally {
      setCheckingAvailability(false);
    }
  };

  // Create booking
  const bookNow = async () => {
    // Validation
    if (!form.checkin || !form.checkout || !form.name || !form.phone) {
      alert("Please fill in all required fields");
      return;
    }

    if (nights <= 0) {
      alert("Check-out date must be after check-in date");
      return;
    }

    setLoading(true);

    try {
      // Create booking in database with calculated total
      const bookingData = {
        checkin: new Date(form.checkin).toISOString(),
        checkout: new Date(form.checkout).toISOString(),
        guests: parseInt(form.guests) || 1,
        cottages: parseInt(form.cottages) || 1,
        room: form.room,
        name: form.name,
        phone: form.phone,
        email: form.email || null,
      };

      const response = await axios.post(`${API}/bookings`, bookingData);

      // Send WhatsApp confirmations to both numbers
      const roomNames = {
        cottage: "Cottage",
        dormitory: "Dormitory",
        full_property: "Full Property",
      };

      const msg = `🏡 White Meadow Booking Confirmed!%0A%0A` +
        `👤 Name: ${form.name}%0A` +
        `📱 Phone: ${form.phone}%0A` +
        `${form.email ? `📧 Email: ${form.email}%0A` : ''}` +
        `🏠 Room: ${roomNames[form.room]}%0A` +
        `${form.room === 'cottage' && form.cottages > 1 ? `🏡 Cottages: ${form.cottages}%0A` : ''}` +
        `📅 Check-in: ${new Date(form.checkin).toLocaleDateString()}%0A` +
        `📅 Check-out: ${new Date(form.checkout).toLocaleDateString()}%0A` +
        `👥 Guests: ${form.guests}%0A` +
        `🌙 Nights: ${nights}%0A` +
        `💰 Total: ₹${total.toLocaleString('en-IN')}`;

      // Open WhatsApp for first number
      window.open(`https://wa.me/${WHATSAPP_NUMBERS[0].replace(/[^0-9]/g, '')}?text=${msg}`, '_blank');

      // Refresh bookings list
      await fetchBookings();

      // Reset form
      setForm({
        checkin: "",
        checkout: "",
        guests: 1,
        cottages: 1,
        room: "cottage",
        name: "",
        phone: "",
        email: "",
      });
      setAvailabilityStatus(null);

      alert("✅ Booking confirmed! WhatsApp message will open.");
    } catch (error) {
      console.error("Error creating booking:", error);
      alert(error.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const roomDetails = {
    cottage: { name: "Cottage", weekday: "₹3,999", weekend: "₹4,999", desc: "Cozy private cottage" },
    dormitory: { name: "Dormitory", weekday: "₹17,999", weekend: "₹19,999", desc: "Sleeps up to 16 (₹1,200/extra person)" },
    full_property: { name: "Full Property", weekday: "₹27,999", weekend: "₹29,999", desc: "Entire resort up to 25 (₹1,200/extra person)" },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
            <Card className="max-w-4xl mx-auto shadow-2xl border-2 border-[#3D2817]/20">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-[#3D2817] mb-6 text-center">
                  Book Your Perfect Stay
                </h2>

                {/* Room Selection */}
                <div className="mb-6">
                  <Label className="text-lg font-semibold text-[#3D2817] mb-3 block">
                    Select Room Type
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(roomDetails).map(([key, room]) => (
                      <motion.div
                        key={key}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Card
                          className={`cursor-pointer transition-all ${
                            form.room === key
                              ? "bg-[#3D2817] text-white shadow-lg"
                              : "bg-white hover:shadow-md"
                          }`}
                          onClick={() => {
                            setForm({ ...form, room: key });
                            setAvailabilityStatus(null);
                          }}
                        >
                          <CardContent className="p-4">
                            <h3 className="font-bold text-lg mb-1">{room.name}</h3>
                            <p className="text-sm mb-2 opacity-90">{room.desc}</p>
                            <div className="flex justify-between text-sm">
                              <span>Weekday: {room.weekday}</span>
                              <span>Weekend: {room.weekend}</span>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Date Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="checkin" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <Calendar className="w-4 h-4" />
                      Check-in Date
                    </Label>
                    <Input
                      type="date"
                      id="checkin"
                      name="checkin"
                      value={form.checkin}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="border-[#3D2817]/30"
                      data-testid="checkin-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="checkout" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <Calendar className="w-4 h-4" />
                      Check-out Date
                    </Label>
                    <Input
                      type="date"
                      id="checkout"
                      name="checkout"
                      value={form.checkout}
                      onChange={handleChange}
                      min={form.checkin || new Date().toISOString().split('T')[0]}
                      className="border-[#3D2817]/30"
                      data-testid="checkout-date-input"
                    />
                  </div>
                </div>

                {/* Guest Count */}
                <div className="mb-6">
                  <Label htmlFor="guests" className="flex items-center gap-2 text-[#3D2817] mb-2">
                    <Users className="w-4 h-4" />
                    Number of Guests
                  </Label>
                  <Input
                    type="number"
                    id="guests"
                    name="guests"
                    value={form.guests}
                    onChange={handleChange}
                    min="1"
                    max="50"
                    className="border-[#3D2817]/30"
                    data-testid="guests-input"
                  />
                </div>

                {/* Number of Cottages (only for cottage bookings) */}
                {form.room === 'cottage' && (
                  <div className="mb-6">
                    <Label htmlFor="cottages" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <Users className="w-4 h-4" />
                      Number of Cottages (Optional)
                    </Label>
                    <Input
                      type="number"
                      id="cottages"
                      name="cottages"
                      value={form.cottages}
                      onChange={handleChange}
                      min="1"
                      max="3"
                      className="border-[#3D2817]/30"
                      data-testid="cottages-input"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      We have 3 cottages available. Each cottage can accommodate guests.
                    </p>
                  </div>
                )}

                {/* Availability Check */}
                {form.checkin && form.checkout && (
                  <div className="mb-6">
                    <Button
                      onClick={checkAvailability}
                      disabled={checkingAvailability}
                      className="w-full bg-[#3D2817] hover:bg-[#2D1F0F] text-white"
                      data-testid="check-availability-btn"
                    >
                      {checkingAvailability ? "Checking..." : "Check Availability"}
                    </Button>

                    {availabilityStatus && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className={`mt-3 p-4 rounded-lg flex items-center gap-2 ${
                          availabilityStatus.available
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {availabilityStatus.available ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : (
                          <XCircle className="w-5 h-5" />
                        )}
                        <span className="font-semibold">{availabilityStatus.message}</span>
                      </motion.div>
                    )}
                  </div>
                )}

                {/* Pricing Summary */}
                {nights > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-[#3D2817] text-white p-6 rounded-lg mb-6"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-lg">Nights:</span>
                      <span className="text-2xl font-bold">{nights}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-lg">Total Amount:</span>
                      <span className="text-3xl font-bold">₹{total.toLocaleString('en-IN')}</span>
                    </div>
                  </motion.div>
                )}

                {/* Guest Details */}
                <div className="space-y-4 mb-6">
                  <div>
                    <Label htmlFor="name" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <User className="w-4 h-4" />
                      Full Name *
                    </Label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className="border-[#3D2817]/30"
                      data-testid="name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <Phone className="w-4 h-4" />
                      Phone Number *
                    </Label>
                    <Input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+91 XXXXX XXXXX"
                      className="border-[#3D2817]/30"
                      data-testid="phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <Mail className="w-4 h-4" />
                      Email (Optional)
                    </Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="border-[#3D2817]/30"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                {/* Confirm Booking Button */}
                <Button
                  onClick={bookNow}
                  disabled={loading || !availabilityStatus?.available}
                  className="w-full bg-gradient-to-r from-[#3D2817] to-[#5D3822] hover:from-[#2D1F0F] hover:to-[#4D2F1A] text-white py-6 text-lg font-bold shadow-lg disabled:opacity-50"
                  data-testid="confirm-booking-btn"
                >
                  {loading ? "Booking..." : "Confirm Booking via WhatsApp"}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
  );
}

export default BookingApp;
